<template>
  <Slideover :visible="visible">
    <form v-on:submit.prevent="save">
      <header class="px-2 py-3 sm:px-6 bg-green-600 text-white">
        <div class="flex items-start justify-between space-x-3">
          <h2 class="text-lg leading-7 font-medium">
            {{ title }}
          </h2>
          <div class="h-7 flex items-center">
            <button
              type="button"
              aria-label="Close panel"
              @click="$emit('toggle-slideover')"
              class="text-green-300 hover:text-white transition ease-in-out duration-150"
            >
              <icon set="solid" name="x" class="flex-shrink-0 m-1 h-5 w-5" />
            </button>
          </div>
        </div>
      </header>

      <!-- Main -->
      <div class="relative flex-1 py-3 px-2 mx-4">
        <div class="grid grid-cols-1 gap-y-2 gap-x-4 sm:grid-cols-6">
          <!-- Amount -->
          <div class="sm:col-span-3">
            <label
              for="amount"
              class="block text-sm font-medium leading-5 text-gray-700"
            >
              Refund Amount
            </label>
            <div class="mt-1 rounded-md shadow-sm">
              <input
                id="amount"
                class="form-input block w-full transition duration-150 ease-in-out sm:text-sm sm:leading-5"
                v-model="form.amount"
                :placeholder="maxRefundAmount != null ? ('Maximum: ' + maxRefundAmount) : 'Fetching max from Shopify...'"
                type="number"
                min="0.00"
                :max="maxRefundAmount"
                step="0.01"
                :disabled="maxRefundAmount == null"
              />
            </div>
          </div>

          <!-- Reason -->
          <div class="sm:col-span-3">
            <label
              for="reason"
              class="block text-sm font-medium leading-5 text-gray-700"
            >
              Reason
            </label>
            <select
              v-if="!reasonsLoaded"
              class="mt-1 block form-select w-full py-2 px-3 border border-gray-300 bg-white rounded-md shadow-sm focus:outline-none focus:shadow-outline-blue focus:border-blue-300 transition duration-150 ease-in-out sm:text-sm sm:leading-5"
              disabled
            >
              <option selected disabled value="">Loading...</option>
            </select>
            <select
              v-else
              id="reason"
              v-model="form.Reason"
              class="mt-1 block form-select w-full py-2 px-3 border border-gray-300 bg-white rounded-md shadow-sm focus:outline-none focus:shadow-outline-blue focus:border-blue-300 transition duration-150 ease-in-out sm:text-sm sm:leading-5"
            >
              <option :value="null">Choose a Reason Code</option>
              <option v-for="reason in reasons" :key="reason.Text1">
                {{ reason.Text1 }}
              </option>
            </select>
          </div>

          <!-- Follow up Task -->
          <div class="sm:col-span-3">
            <label
              for="follow"
              class="block text-sm font-medium leading-5 text-gray-700"
            >
              Follow up Task
            </label>
            <div class="mt-1 rounded-md shadow-sm">
              <input
                id="follow"
                class="form-input block w-full transition duration-150 ease-in-out sm:text-sm sm:leading-5"
                :placeholder="form.FollowUp"
                :disabled="true"
              />
            </div>
          </div>

          <!-- Policy -->
          <div class="sm:col-span-3">
            <label
              for="policy"
              class="block text-sm font-medium leading-5 text-gray-700"
            >
              Policy
            </label>
            <div class="mt-1 rounded-md shadow-sm">
              <input
                id="policy"
                class="form-input block w-full transition duration-150 ease-in-out sm:text-sm sm:leading-5"
                :placeholder="form.Policy"
                :disabled="true"
              />
            </div>
          </div>

          <!-- Note -->
          <div class="sm:col-span-6">
            <label
              for="note"
              class="block text-sm font-medium leading-5 text-gray-700"
            >
              Note
            </label>
            <div class="mt-1 rounded-md shadow-sm">
              <textarea
                id="note"
                v-model="form.Note"
                rows="3"
                class="form-textarea block w-full transition duration-150 ease-in-out sm:text-sm sm:leading-5"
              ></textarea>
            </div>
          </div>
        </div>
      </div>

      <HttpError :error="error" />

      <!-- Footer -->
      <div class="flex-shrink-0 px-4 py-4 space-x-4 flex justify-end">
        <span class="inline-flex rounded-md shadow-sm">
          <button
            type="button"
            @click="$emit('toggle-slideover')"
            class="py-2 px-4 border border-gray-300 rounded-md text-sm leading-5 font-medium text-gray-700 hover:text-gray-500 focus:outline-none focus:border-blue-300 focus:shadow-outline-blue active:bg-gray-50 active:text-gray-800 transition duration-150 ease-in-out"
          >
            Cancel
          </button>
        </span>
        <span class="inline-flex rounded-md shadow-sm">
          <Button
            type="submit"
            :disabled="saving || missingInput"
            class="flex items-center space-x-2"
          >
            <span>Save</span>
            <LoadingSpinner v-if="saving" class="text-white" />
          </Button>
        </span>
      </div>
    </form>
  </Slideover>
</template>
<script>
import { ApiFactory } from "@/api/ApiFactory.js";
import Slideover from "@/components/layout/Slideover.vue";
import Button from "@/components/elements/Button.vue";

const Listing = ApiFactory.get("listings");
const OrdersAPI = ApiFactory.get("orders");

export default {
  name: "OrderSearch",
  data() {
    return {
      reasons: [],
      reasonsLoaded: false,
      saving: false,
      error: null,
      form: {
        adjustment: "Shipping",
        amount: null,
        Reason: null,
        FollowUp: "Refund Issued - Partial",
        Policy: "Customer Care",
        Note: null,
      },
      maxRefundAmount: null,
    };
  },
  props: {
    title: {
      type: String,
      default: "Title",
    },
    footer: {
      type: Boolean,
      default: true,
    },
    visible: {
      type: Boolean,
      default: false,
    },
  },
  created() {
    Listing.get({ list: "Adjustment Reasons - Shipping" }).then((response) => {
      this.reasons = response.data.body;
      this.reasonsLoaded = true;
    });

    OrdersAPI.getCalculatedRefund(this.$store.getters["orders/getOrder"].SourceOrderNumber).then((response) => {
      this.maxRefundAmount = response.data.body.shipping.maximum_refundable;
    });
  },
  components: {
    Slideover,
    Button,
  },
  computed: {
    missingInput() {
      if (
        this.form.amount <= 0 ||
        this.form.Note?.trim() == "" ||
        this.form.Note?.trim() == undefined ||
        this.form.Reason?.trim() == "" ||
        this.form.Reason?.trim() == undefined
      )
        return true;
      return false;
    },
  },
  methods: {
    save() {
      this.saving = true;
      this.error = null;

      const order = this.$store.getters["orders/getOrder"];
      this.$store
        .dispatch("orders/editTotals", {
          OrderNumber: order.SourceOrderNumber,
          data: this.form,
        })
        .then(() => {
          this.$emit("onClose");
        })
        .catch((e) => (this.error = e))
        .finally(() => {
          this.saving = false;
        });
    },
  },
  mounted() {},
  filters: {},
};
</script>
